package app.nestbridge.feature.invitations.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.LinkAnnotation
import androidx.compose.ui.text.TextLinkStyles
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.karawek.core.ui.components.CorePrimaryButton
import com.karawek.core.ui.components.CoreSecondaryButton
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreSpanStyles
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import app.nestbridge.feature.invitations.screen.InvitationConfirmationEvent
import app.nestbridge.feature.invitations.screen.InvitationConfirmationScreen
import app.nestbridge.feature.invitations.screen.InvitationConfirmationState
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import nestbridge.composeapp.generated.resources.Res
import nestbridge.composeapp.generated.resources.ic_nestbridge_logo
import org.jetbrains.compose.resources.painterResource
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import com.karawek.core.ui.components.CoreScaffold
import app.nestbridge.feature.invitations.screen.InvitationDetailEvent
import app.nestbridge.feature.invitations.screen.InvitationDetailState
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(
    screen = InvitationConfirmationScreen::class,
    scope = AppScope::class,
)
@Composable
fun InvitationConfirmationUi(
    state: InvitationConfirmationState,
    modifier: Modifier = Modifier,
) {
    CoreScaffold(
        scrollable = false,
        footer = { Footer(state) },
        onAppearKey = state.mode.name,
        modifier = modifier,
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
            modifier = Modifier.fillMaxSize(),
        ) {
            Box(
                modifier = Modifier.height(60.dp),
                contentAlignment = Alignment.Center,
            ) {
                Image(
                    painter = painterResource(Res.drawable.ic_nestbridge_logo),
                    contentDescription = "NestBridge logo",
                )
            }

            Spacer(modifier = Modifier.height(CoreSpacing.lg))

            when (state.mode) {
                InvitationConfirmationScreen.Mode.Accept -> {
                    Text(
                        text = "Accepting will connect you to this care space.",
                        style = CoreTypography.body,
                        color = CoreTheme.colors.textPrimary,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth(),
                    )

                    Spacer(modifier = Modifier.height(CoreSpacing.md))

                    Text(
                        text = "You’ll be able to view shared notes, updates, and collaborate with the care team.",
                        style = CoreTypography.bodySecondary,
                        color = CoreTheme.colors.textSecondary,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth(),
                    )

                    Text(
                        text = buildAnnotatedString {
                            append("By continuing, you agree to our Terms of Service and Privacy Policy.")

                            addLink(
                                clickable = LinkAnnotation.Clickable(
                                    tag = "Terms of Service",
                                    styles = TextLinkStyles(
                                        style = CoreSpanStyles.inlineLink,
                                    ),
                                    linkInteractionListener = { state.eventSink(InvitationConfirmationEvent.ShowTerms) }
                                ),
                                start = 32,
                                end = 48,
                            )

                            addLink(
                                clickable = LinkAnnotation.Clickable(
                                    tag = "Privacy Policy",
                                    styles = TextLinkStyles(
                                        style = CoreSpanStyles.inlineLink,
                                    ),
                                    linkInteractionListener = { state.eventSink(InvitationConfirmationEvent.ShowTerms) }
                                ),
                                start = 53,
                                end = 67,
                            )
                        },
                        style = CoreTypography.bodySecondary,
                        color = CoreTheme.colors.textSecondary,
                        textAlign = TextAlign.Center,
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(top = CoreSpacing.md),
                    )
                }

                InvitationConfirmationScreen.Mode.Decline -> {
                    Text(
                        text = "Declining means you won’t be connected through this invitation.",
                        style = CoreTypography.body,
                        color = CoreTheme.colors.textPrimary,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth(),
                    )
                    Spacer(modifier = Modifier.height(CoreSpacing.md))
                    Text(
                        text = "You can still receive a new invitation later.",
                        style = CoreTypography.bodySecondary,
                        color = CoreTheme.colors.textSecondary,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
            }
        }
    }
}

@Composable
private fun Footer(state: InvitationConfirmationState) =
    Column(
        modifier = Modifier
            .fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
    ) {
        CorePrimaryButton(
            text = when (state.mode) {
                InvitationConfirmationScreen.Mode.Accept -> "Accept invitation"
                InvitationConfirmationScreen.Mode.Decline -> "Decline invitation"
            },
            onClick = { state.eventSink(InvitationConfirmationEvent.Confirm) },
            modifier = Modifier.fillMaxWidth(),
        )

        CoreSecondaryButton(
            text = "Decide later",
            onClick = { state.eventSink(InvitationConfirmationEvent.DecideLater) },
            modifier = Modifier.fillMaxWidth(),
        )
    }

// region Previews

@Preview
@Composable
private fun AcceptPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        InvitationConfirmationUi(
            state = InvitationConfirmationState(
                mode = InvitationConfirmationScreen.Mode.Accept,
            ),
            modifier = Modifier.background(color = CoreTheme.colors.background),
        )
    }
}

@Preview
@Composable
private fun DeclinePreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        InvitationConfirmationUi(
            state = InvitationConfirmationState(
                mode = InvitationConfirmationScreen.Mode.Decline,
            ),
            modifier = Modifier.background(color = CoreTheme.colors.background),
        )
    }
}

// endregion
